import type {
  CardFormat,
  ChartCard,
  TrendDirection,
} from '@/features/dashboard/api/schemas'

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
}

const MAGNITUDES = [
  { size: 1e9, suffix: 'B' },
  { size: 1e6, suffix: 'M' },
  { size: 1e3, suffix: 'K' },
] as const

const currencySymbol = (unit: string | null | undefined) =>
  unit ? CURRENCY_SYMBOLS[unit] : undefined

/** Fractional change between two values, or null when it can't be derived. */
export const changeFraction = (
  value: number,
  previousValue: number | null | undefined
): number | null =>
  previousValue == null || previousValue === 0
    ? null
    : (value - previousValue) / Math.abs(previousValue)

/** e.g. 0.0593 -> '+5.9%'. */
export const formatChange = (fraction: number): string => {
  const sign = fraction > 0 ? '+' : fraction < 0 ? '−' : ''
  return `${sign}${(Math.abs(fraction) * 100).toFixed(1)}%`
}

export const trendFromChange = (fraction: number | null): TrendDirection =>
  fraction === null || fraction === 0 ? 'flat' : fraction > 0 ? 'up' : 'down'

type ValueParts = {
  /** Sign and currency symbol, in that order, e.g. '-€'. */
  prefix: string
  main: string
  /** Magnitude letter (K/M/B), rendered smaller next to the figure. */
  suffix: string
  /** Trailing unit for non-currency units, e.g. '%'. */
  unit: string
}

export type ValueOptions = {
  unit?: string | null | undefined
  format?: CardFormat | undefined
  /**
   * kpi: an abbreviated value keeps one decimal (9.9M, as bristol's
   * `f'{v:.1f}M'`). chart: it is a whole number (€557M).
   */
  kind?: 'kpi' | 'chart'
}

const ISO_CURRENCY = /^[A-Z]{3}$/

const currencyPrefix = (unit: string | null | undefined) => {
  if (!unit) return ''
  return CURRENCY_SYMBOLS[unit] ?? `${unit} `
}

/**
 * A headline figure in pieces, as bristol's cockpit formats it: percent cards
 * hold fractions and always show one decimal (0.1217 -> '12.2%', whatever
 * metabase's own decimals say); other values abbreviate as K/M/B, a value that
 * rounds to 1000 of its magnitude carrying up (999.6K -> 1M). Below 1000 a
 * chart value is a whole number, while a kpi integer is plain and anything
 * else has 2 decimals.
 */
export const formatValueParts = (
  value: number,
  { unit, format, kind = 'chart' }: ValueOptions = {}
): ValueParts => {
  const style = format?.style ?? 'number'
  const abs = Math.abs(value)

  if (style === 'percent') {
    const main = (abs * 100).toFixed(1)
    return {
      prefix: value < 0 && Number(main) !== 0 ? '-' : '',
      main,
      suffix: '',
      unit: '%',
    }
  }

  const sign = value < 0 ? '-' : ''
  const prefix = `${sign}${style === 'currency' ? currencyPrefix(unit) : ''}`
  const trailing =
    style === 'number' && unit && !ISO_CURRENCY.test(unit) ? unit : ''
  if (abs < 1000) {
    const main =
      kind === 'chart' || Number.isInteger(abs)
        ? String(Math.round(abs))
        : abs.toFixed(2)
    return { prefix, main, suffix: '', unit: trailing }
  }

  const digits = kind === 'kpi' ? 1 : 0
  const scaled = (size: number) => Number((abs / size).toFixed(digits))
  let index = MAGNITUDES.findIndex(({ size }) => abs >= size)
  if (index > 0 && scaled(MAGNITUDES[index]?.size ?? 1) >= 1000) index -= 1
  const magnitude = MAGNITUDES[index]
  return {
    prefix,
    main: (abs / (magnitude?.size ?? 1)).toFixed(digits),
    suffix: magnitude?.suffix ?? '',
    unit: trailing,
  }
}

/** The figure as plain text, e.g. '€557M' or '12%'. */
export const formatValue = (value: number, options?: ValueOptions): string => {
  const { prefix, main, suffix, unit } = formatValueParts(value, options)
  return `${prefix}${main}${suffix}${unit}`
}

/** A chart card's per-category totals, falling back to its first series. */
export const chartTotals = (
  card: Pick<ChartCard, 'totals' | 'series'>
): (number | null)[] =>
  card.totals.length > 0 ? card.totals : (card.series[0]?.data ?? [])

/** Axis/label format matching bristol's `abbrev`, e.g. 7200000 -> '€7.2M'. */
export const formatAbbrev = (
  value: number,
  unit?: string | null | undefined,
  decimals = 1
): string => {
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  const symbol = currencySymbol(unit) ?? ''
  const magnitude = MAGNITUDES.find(({ size }) => abs >= size)
  if (!magnitude) return `${sign}${symbol}${abs.toFixed(0)}`
  return `${sign}${symbol}${(abs / magnitude.size).toFixed(decimals)}${magnitude.suffix}`
}

const MONTH_PATTERN = /^(\d{4})-(\d{2})/
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const parseMonth = (category: string) => {
  const match = MONTH_PATTERN.exec(category)
  const month = match && MONTHS[Number(match[2]) - 1]
  return match?.[1] && month ? { year: match[1], month } : null
}

/** e.g. '2026-07' -> 'Jul 26'; anything that isn't a month passes through. */
export const formatMonthShort = (category: string): string => {
  const parsed = parseMonth(category)
  return parsed
    ? `${parsed.month.slice(0, 3)} ${parsed.year.slice(2)}`
    : category
}

/** e.g. '2026-07' -> 'July 2026'; anything that isn't a month passes through. */
export const formatMonthLong = (category: string): string => {
  const parsed = parseMonth(category)
  return parsed ? `${parsed.month} ${parsed.year}` : category
}

/**
 * Sentence case, keeping acronyms, as bristol titles a deep dive:
 * 'Revenue Deep Dive' -> 'Revenue deep dive', 'PAU Deep Dive' -> 'PAU deep dive'.
 */
export const sentenceCase = (title: string) =>
  title.replace(
    / (?![A-Z]{2,}\b)(\w+)/g,
    (_, word: string) => ` ${word.toLowerCase()}`
  )
